/**
 * The rule screen's body (Figma 2:2, 4:2): `RuleBody` is the two columns under the bar (the printed page's pane
 * on the raised ground, and the rail beside it, 520 wide), `PaneHead` the pane's title, note and the chip at its
 * end, `PaneScroll` the scrolling ground the page card stands on, `PageCard` the white card the print sits in,
 * `RailSection` one padded band of the rail (a wash for the fix tray) and `RailGap` the spare height that keeps
 * the verdict at the rail's foot.
 */
import type { ReactNode } from 'react';
import { RULE, SPACE, TEXT, WEIGHT, usePalette } from '../theme';

export function RuleBody({ pane, rail }: { pane: ReactNode; rail: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: p.surfaceRaised }}>{pane}</div>
      <aside
        aria-label="Rule"
        style={{
          width: RULE.rail,
          flexShrink: 0,
          boxSizing: 'border-box',
          borderLeft: `1px solid ${p.hairline}`,
          background: p.surface,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {rail}
      </aside>
    </div>
  );
}

export function PaneHead({ title, note, end }: { title: ReactNode; note: ReactNode; end?: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.snug, padding: `${SPACE.sm}px ${SPACE.lg}px`, background: p.surface, flexShrink: 0 }}>
      <h2 style={{ margin: 0, fontSize: TEXT.body, fontWeight: WEIGHT.strong, color: p.ink, lineHeight: 'normal' }}>{title}</h2>
      <span style={{ fontSize: TEXT.body, color: p.inkMuted, lineHeight: 'normal' }}>{note}</span>
      <span style={{ flex: 1 }} />
      {end}
    </div>
  );
}

export function PaneScroll({ children }: { children: ReactNode }) {
  return (
    <div
      role="region"
      aria-label="Printed page"
      tabIndex={0}
      style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: `${SPACE.wide}px ${SPACE.xl}px` }}
    >
      {children}
    </div>
  );
}

export function PageCard({ children }: { children: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ width: RULE.sheet, maxWidth: '100%', flexShrink: 0, boxSizing: 'border-box', background: p.surface, border: `1px solid ${p.hairline}`, borderRadius: SPACE.tight, padding: SPACE.lg }}>
      {children}
    </div>
  );
}

/** One band of the rail, padded as the frame pads it; `wash` paints it on the amber ground of the fix tray, `bare` leaves off the hairline above it (the rail's first band). */
export function RailSection({ children, wash = false, bare = false, label }: { children: ReactNode; wash?: boolean; bare?: boolean; label?: string }) {
  const p = usePalette();
  return (
    <section
      aria-label={label}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.snug,
        flexShrink: 0,
        padding: `${SPACE.cozy}px ${SPACE.wide}px`,
        background: wash ? p.absenceWash : 'transparent',
        borderTop: bare ? undefined : `1px solid ${p.hairline}`,
        boxSizing: 'border-box',
      }}
    >
      {children}
    </section>
  );
}

/** The rail's spare height: the verdict beneath it stays at the foot. */
export function RailGap() {
  return <div style={{ flex: 1, minHeight: 0 }} />;
}

/** The readings under the page (Figma 2:2, 2:90): the title, the chips of the lanes that read it, and a hint at the end. */
export function ReadingsBar({ title, chips, hint }: { title: ReactNode; chips: ReactNode; hint: ReactNode }) {
  const p = usePalette();
  return (
    <section
      aria-label="Readings"
      style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexShrink: 0, padding: `${SPACE.snug}px ${SPACE.lg}px`, background: p.surface, borderTop: `1px solid ${p.hairline}` }}
    >
      <h3 style={{ margin: 0, fontSize: TEXT.body, fontWeight: WEIGHT.strong, color: p.ink, lineHeight: 'normal' }}>{title}</h3>
      {chips}
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: TEXT.caption, color: p.inkMuted, lineHeight: 'normal' }}>{hint}</span>
    </section>
  );
}
